import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import { Listing, Role } from '@curbnturf/entities';
import { ListingApiService } from '../backend-api/listing-api.service';
import { AuthService, IPublicUser } from '../auth/auth.service';
import { addOnsFromEntity, toPrivateListingFromEntity } from '../listings/listing-entity.mapper';
import { IAddOn, IPrivateListing } from '../listings/mock-listings.data';

/**
 * Real-backed "which listings does this host own" — talks to curbnturf's
 * `GET listing?userId=` via the plain, NgRx-free `ListingService`
 * (`@curbnturf/listing`, deep-imported for the same reason `MessageService`/
 * `FavoritesService` deep-import their own real services). Replaces the
 * localStorage-shadowed `getMyListings()` in `mock-host-data.ts`.
 *
 * Mirrors `FavoritesService`'s shape exactly: hydrate on auth change, no
 * mock fallback, no polling — listing ownership only ever changes through
 * this client's own publish/delete actions (`HostListingDraftService` calls
 * `refreshNow()` directly after each), unlike messages which arrive from
 * another party and need a poll.
 */
@Injectable({ providedIn: 'root' })
export class HostListingsService {
  private auth = inject(AuthService);
  private listingSvc = inject(ListingApiService);

  private readonly _myListings$ = new BehaviorSubject<IPrivateListing[]>([]);
  readonly myListings$: Observable<IPrivateListing[]> = this._myListings$.asObservable();

  /**
   * Real add-ons per listing id, from the same fetch (F12).
   *
   * `refreshNow()` already has the full `Listing` entities in hand — extras
   * included — and was throwing them away in the mapping step, because
   * `IPrivateListing` is the flat marketing shape and has no add-on field. Three
   * host-dashboard call sites then reached for `getListingDetail()` to get them
   * back, which is the placeholder stub and returns `[]`: the add-on performance
   * widget was empty, the add-on → property map was empty, and the per-listing
   * breakdown always reported `offered: false`.
   *
   * Keeping them here costs no extra HTTP and keeps `IPrivateListing` flat.
   */
  private readonly _myListingAddOns$ = new BehaviorSubject<Map<number, IAddOn[]>>(new Map());
  readonly myListingAddOns$: Observable<Map<number, IAddOn[]>> = this._myListingAddOns$.asObservable();

  /** Synchronous accessor, for the same reason `list()` exists. */
  addOnsFor(listingId: number): IAddOn[] {
    return this._myListingAddOns$.value.get(listingId) ?? [];
  }

  constructor() {
    this.auth.currentUser$.subscribe((user) => {
      if (user?.id) void this.refreshNow();
      else this.clearCache();
    });
  }

  async refreshNow(): Promise<void> {
    const userId = this.auth.currentUser?.id;
    if (!userId) {
      this.clearCache();
      return;
    }
    try {
      // pageSize defaults to 100 — fine for any realistic host today, revisit if that assumption breaks.
      const { listings } = await firstValueFrom(this.listingSvc.fetchAll({ userId }));
      const entities = listings as unknown as Listing[];
      const mapped = entities.map((l) => toPrivateListingFromEntity(l)).filter((l): l is IPrivateListing => l !== null);
      // Same entities, before they are flattened — see myListingAddOns$.
      const addOns = new Map<number, IAddOn[]>();
      for (const l of entities) {
        if (l.id != null) addOns.set(l.id, addOnsFromEntity(l));
      }
      this._myListingAddOns$.next(addOns);
      this._myListings$.next(mapped);
    } catch (err) {
      console.error('[host-listings] failed to refresh', err);
    }
  }

  /** Both caches drop together — a stale add-on map outliving a sign-out would
   *  attribute one host's extras to whoever signs in next. */
  private clearCache(): void {
    this._myListings$.next([]);
    this._myListingAddOns$.next(new Map());
  }

  list(): IPrivateListing[] {
    return this._myListings$.value;
  }

  /** Fetch any single listing by id (not scoped to "my own") — for pages
   *  (e.g. a per-listing calendar) that need to display a listing before
   *  separately checking ownership via `list()`/`isOwner()`. Real
   *  `GET /listing/:id`, mapped the same way as `refreshNow()`. */
  fetch(listingId: number): Observable<IPrivateListing | null> {
    return this.listingSvc.fetch(listingId).pipe(map((l) => toPrivateListingFromEntity(l as unknown as Listing)));
  }

  isOwner(listingId: number): boolean {
    return this._myListings$.value.some((l) => l.id === listingId);
  }

  hasAnyListings(): boolean {
    return this._myListings$.value.length > 0;
  }

  /**
   * Authoritative, uncached ownership check — a direct GET /listing/:id, not
   * the myListings$ cache. Used only by editOwnerGuard, where a not-yet-
   * hydrated cache on a cold/deep-link navigation must never produce a false
   * negative.
   *
   * Grants edit access to: the real owner; an admin (unconditionally); or a
   * sales/salesManager rep for their own referred client's listing — but
   * ONLY while it's still unpublished (`available === false`). Once a
   * listing goes live, only the owner or an admin can edit it — the rep's
   * affiliateRef relationship to the client doesn't change, but their edit
   * window closes. Fails closed (false) if the listing or its owner's
   * `source` can't be read.
   */
  async checkOwnership(listingId: number, user: IPublicUser): Promise<boolean> {
    try {
      const listing = await firstValueFrom(this.listingSvc.fetch(listingId));
      if (!listing) return false;
      if (listing.user?.id === user.id) return true;
      if (user.role === Role.admin) return true;

      const isSalesRole = user.role === Role.sales || (user.role?.toLowerCase() as Role) === Role.salesManager;
      if (
        isSalesRole &&
        user.affiliateRef &&
        listing.user?.source &&
        user.affiliateRef === listing.user.source &&
        listing.available === false
      ) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Publishes a listing (`available: true`) via the real `POST listing/:id/enable`
   * endpoint — server-side `canEnable()` validation can reject this (e.g. an
   * incomplete listing), surfaced back via `message`. Refreshes `myListings$`
   * on success so the row picks up the new state immediately.
   */
  async publish(listingId: number): Promise<{ success: boolean; message: string }> {
    const result = await firstValueFrom(this.listingSvc.enable({ id: listingId } as Listing));
    if (result.success) await this.refreshNow();
    return result;
  }

  /** Hides a listing (`available: false`) via `POST listing/:id/disable`. Refreshes `myListings$` on success. */
  async hide(listingId: number): Promise<{ success: boolean; message: string }> {
    const result = await firstValueFrom(this.listingSvc.disable({ id: listingId } as Listing));
    if (result.success) await this.refreshNow();
    return result;
  }
}
